import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  List,
  ListItem,
  ListItemText,
  Switch,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Paper
} from '@mui/material'
import {
  VisibilityOffOutlined as NoAccessIcon,
  VisibilityOutlined as ReadOnlyIcon,
  EditOutlined as EditIcon
} from '@mui/icons-material'

import { setRolePermission } from 'store/settings/role'
import { useSession } from 'components/Providers'

function ListHeader({ module }) {
  const { appearance } = useSession();
  return (
    <Box sx={{ px: 3, py: 2, bgcolor: appearance === 'dark' ? 'grey.800' : 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight="700" color="text.primary">
        {module.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {module.description}
      </Typography>
    </Box>
  )
}

function PermAction({ perm, role, appearance }) {
  const [value, setValue] = useState(perm.value)
  const dispatch = useDispatch()

  useEffect(() => {
    setValue(perm.value || 0)
  }, [role.id, perm.id, perm.value])

  const save = async newValue => {
    if (newValue === null) return

    const payload = { role: role.id, permission: perm.id, value: newValue }
    const { error, payload: res } = await dispatch(setRolePermission(payload))
    if (!error) {
      setValue(res.value)
    }
  }

  if (perm.type === 'xmodel')
    return (
      <Select
        size="small"
        value={value}
        onChange={e => save(e.target.value)}
        sx={{ minWidth: 140, borderRadius: 2, bgcolor: 'white' }}
      >
        <MenuItem value={0}>No Access</MenuItem>
        <MenuItem value={1}>Read Only</MenuItem>
        <MenuItem value={15}>Full Control</MenuItem>
      </Select>
    )

  if (perm.type === 'model')
    return (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(e, v) => save(v)}
        sx={{ bgcolor: 'background.paper', '& .MuiToggleButton-root': { py: 0.5, px: 1.5 } }}
      >
        <ToggleButton value={0} aria-label="none">
          <NoAccessIcon fontSize="small" sx={{ color: appearance === 'dark' ? 'grey.500' : 'disabled' }} />
        </ToggleButton>
        <ToggleButton value={1} aria-label="read">
          <ReadOnlyIcon fontSize="small" sx={{ color: appearance === 'dark' ? 'grey.500' : 'disabled' }} />
        </ToggleButton>
        <ToggleButton value={15} aria-label="edit">
          <EditIcon fontSize="small" sx={{ color: appearance === 'dark' ? 'grey.500' : 'disabled' }} />
        </ToggleButton>
      </ToggleButtonGroup>
    )

  return <Switch size="small" checked={!!value} onChange={(e, v) => save(v ? 1 : 0)}  />
}

function PermissionCheck({ perm, role, }) {
  const { appearance } = useSession();

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid',
        borderColor: 'grey.100',
        py: 1.5,
        px: 3,
        '&:hover': { bgcolor: 'background.paper' },
        '&:last-child': { borderBottom: 0 }
      }}
    >
      <ListItemText
        primary={perm.name}
        secondary={perm.description}
        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'text.primary' }}
        secondaryTypographyProps={{ fontSize: '0.8rem' }}
      />
      <Box sx={{ ml: 2 }}>
        <PermAction perm={perm} role={role}  appearance={appearance} />
      </Box>
    </ListItem>
  )
}

export default function PermissionList({ role, module,  }) {

  return (
    <Paper elevation={0} variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}>
      <ListHeader module={module} />
      <List disablePadding>
        {module.permissions.map(perm => (
          <PermissionCheck key={perm.id} role={role} perm={perm} />
        ))}
      </List>
    </Paper>
  )
}